import barParfumImg from "@/assets/bar-parfum-atelier.jpg";
import { createFileRoute, Link } from "@tanstack/react-router";
import receptionImg from "@/assets/dj-mariage-reception.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { DISPONIBILITES, DISPO_LABELS, SAISON_LABEL } from "@/data/disponibilites";
import { TldrBox } from "@/components/tldr-box";
import { PageHero } from "@/components/page-hero";
import { SeoAccordion } from "@/components/seo-accordion";
import { RelatedLinks } from "@/components/related-links";
import { DJ_VAL_SECTIONS } from "@/data/seo-blocks";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
const PRICING_FAQ = [
  {
    q: "Vos tarifs incluent-ils la TVA ?",
    a: "DJ Val bénéficie de la franchise en base de TVA (article 293 B du CGI). Les prix annoncés sont nets, sans TVA additionnelle.",
  },
  {
    q: "Quel est le montant de l'acompte ?",
    a: "Un acompte de 30 % est demandé à la signature du contrat pour bloquer définitivement votre date. Le solde est versé le jour de la prestation ou par virement la semaine qui suit.",
  },
  {
    q: "Les frais de déplacement sont-ils inclus ?",
    a: "Aucun supplément dans un rayon de 80 km autour de Cavaillon. Au-delà, un forfait kilométrique transparent (0,60 €/km) est appliqué et annoncé sur le devis.",
  },
  {
    q: "Quels moyens de paiement acceptez-vous ?",
    a: "Virement bancaire, chèque, espèces. Paiement en 2 fois possible (acompte + solde). Une facture vous est systématiquement remise.",
  },
  {
    q: "Que se passe-t-il en cas d'annulation ?",
    a: "En cas d'annulation à votre initiative à plus de 90 jours de l'événement : remboursement intégral hors frais administratifs. Entre 90 et 30 jours : 50 % de l'acompte conservé. À moins de 30 jours : acompte conservé. En cas de force majeure (santé, deuil), une nouvelle date est proposée gratuitement.",
  },
  {
    q: "Une assurance professionnelle est-elle souscrite ?",
    a: "Oui. DJ Val est couvert en responsabilité civile professionnelle (Hiscox) et déclaré à la SACEM pour la diffusion légale de musique. Attestations fournies sur simple demande.",
  },
];

export const Route = createFileRoute("/tarifs")({
  head: () => ({
    meta: [
      { title: "Tarifs DJ mariage Vaucluse : 3 formules | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Tarifs DJ mariage et événements dans le Vaucluse à partir de 690 €. 3 formules transparentes : Essentielle, Signature, Prestige. Devis gratuit sous 24h.")
      },
      { property: "og:title", content: "Tarifs DJ Mariage Vaucluse | DJ Val" },
      {
        property: "og:description",
        content: metaDesc("Formules Essentielle (690€), Signature mariage (1490€), Prestige (2190€). Devis gratuit sous 24h.")
      },
      { property: "og:url", content: "https://dj-val.fr/tarifs" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/tarifs" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: PRICING_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "DJ Mariage & Événements — DJ Val",
          serviceType: "DJ Mariage & Événements",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: [
            { "@type": "City", name: "Cavaillon" },
            { "@type": "City", name: "Avignon" },
            { "@type": "AdministrativeArea", name: "Vaucluse" },
            { "@type": "AdministrativeArea", name: "Bouches-du-Rhône" },
            { "@type": "AdministrativeArea", name: "Provence" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: "690",
            highPrice: "2190",
            offerCount: 3,
            offers: [
              {
                "@type": "Offer",
                name: "Formule Essentielle",
                description: "5h DJ, sono 80 personnes, lumière LED, micro HF",
                url: "https://dj-val.fr/tarifs#essentielle",
                priceCurrency: "EUR",
                price: "690",
                availability: "https://schema.org/InStock",
                priceSpecification: {
                  "@type": "PriceSpecification",
                  price: "690",
                  priceCurrency: "EUR",
                  valueAddedTaxIncluded: false,
                  description: "À partir de — TVA non applicable, art. 293 B CGI",
                },
              },
              {
                "@type": "Offer",
                name: "Formule Signature",
                description: "8h DJ, sono cérémonie + soirée, mise en lumière complète",
                url: "https://dj-val.fr/tarifs#signature",
                priceCurrency: "EUR",
                price: "1490",
                availability: "https://schema.org/InStock",
                priceSpecification: {
                  "@type": "PriceSpecification",
                  price: "1490",
                  priceCurrency: "EUR",
                  valueAddedTaxIncluded: false,
                  description: "À partir de — TVA non applicable, art. 293 B CGI",
                },
              },
              {
                "@type": "Offer",
                name: "Formule Prestige",
                description: "10h, sono multi-zones, lyres + lasers, étincelles froides, photobooth",
                url: "https://dj-val.fr/tarifs#prestige",
                priceCurrency: "EUR",
                price: "2190",
                availability: "https://schema.org/InStock",
                priceSpecification: {
                  "@type": "PriceSpecification",
                  price: "2190",
                  priceCurrency: "EUR",
                  valueAddedTaxIncluded: false,
                  description: "À partir de — TVA non applicable, art. 293 B CGI",
                },
              },
            ],
          },
        }),
      },
    ],
  }),
  component: Page,
});


const PACKS = [
  {
    name: "Essentielle",
    intro: "Pour soirée privée ou anniversaire",
    price: "à partir de 690 €",
    duration: "5 h",
    capacity: "Jusqu'à 80 invités",
    features: [
      "5h de prestation DJ",
      "Sonorisation pour 80 personnes",
      "Éclairage piste de danse LED",
      "Micro HF pour discours",
      "Playlist personnalisée",
    ],
  },
  {
    name: "Signature",
    intro: "Notre formule mariage la plus demandée",
    price: "à partir de 1 490 €",
    duration: "8 h",
    capacity: "Jusqu'à 150 invités",
    features: [
      "Animation 8h · vin d'honneur au bal",
      "Sonorisation cérémonie + soirée",
      "Mise en lumière de la salle",
      "Machine à fumée légère ouverture de bal",
      "Coordination prestataires",
      "Repérage du lieu inclus",
    ],
    featured: true,
  },
  {
    name: "Prestige",
    intro: "Mariage haut de gamme & événement VIP",
    price: "à partir de 2 190 €",
    duration: "10 h",
    capacity: "Jusqu'à 250 invités",
    features: [
      "Animation complète jusqu'à 3h du matin",
      "Sonorisation multi-zones",
      "Éclairage scénique premium (lyres LED + lasers)",
      "Étincelles froides ouverture de bal",
      "Photobooth ou bar à vinyles",
      "Vidéoprojection diaporamas",
      "Double matériel de secours + technicien",
    ],
  },
];

const COMPARE_ROWS: { label: string; values: [string, string, string] }[] = [
  { label: "Durée d'animation", values: ["5 h", "8 h", "10 h"] },
  { label: "Capacité sono", values: ["80 invités", "150 invités", "250 invités"] },
  { label: "Sonorisation cérémonie laïque", values: ["—", "✓", "✓"] },
  { label: "Éclairage LED dancefloor", values: ["✓", "✓", "✓"] },
  { label: "Lyres LED motorisées", values: ["—", "✓", "✓"] },
  { label: "Lasers + machine brouillard lourd", values: ["—", "—", "✓"] },
  { label: "Étincelles froides ouverture de bal", values: ["—", "Option", "✓"] },
  { label: "Photobooth avec impressions", values: ["—", "Option", "✓"] },
  { label: "Repérage technique du lieu", values: ["—", "✓", "✓"] },
  { label: "Coordination prestataires", values: ["—", "✓", "✓"] },
  { label: "Second technicien", values: ["—", "—", "✓"] },
  { label: "Matériel doublé de secours", values: ["✓", "✓", "✓"] },
];

const ADDONS = [
  { name: "Cérémonie laïque sonorisée", price: "+ 180 €", desc: "Micros HF, retours pour officiants, musique d'entrée/sortie" },
  { name: "Photobooth numérique", price: "+ 190 €", desc: "Borne tactile, photos illimitées, envoi numérique, accessoires inclus" },
  { name: "Photobooth avec impressions", price: "+ 390 €", desc: "Tirages 10×15 illimités, fond personnalisé — livraison, installation et démontage offerts avec une prestation DJ" },
  { name: "Étincelles froides", price: "+ 150 €", desc: "Machines pyro froides pour ouverture de bal" },
  { name: "Guirlandes guinguette", price: "+ 250 €", desc: "Installation et démontage, 30 mètres d'ampoules à filament" },
  { name: "Vidéoprojection diaporamas", price: "+ 200 €", desc: "Vidéoprojecteur full HD + écran ou mur projection" },
  { name: "Déplacement > 50 km", price: "0,60 €/km", desc: "Forfait kilométrique aller-retour depuis Cavaillon" },
];

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={receptionImg}
        eyebrow="Nos formules"
        title={<>Tarifs <span className="italic">DJ mariage</span><span className="block">&amp; événements</span></>}
        subtitle="Trois formules transparentes — Essentielle, Signature, Prestige. Aucun coût caché, devis personnalisé sous 24 h."
        primary={{ label: "✦ Calculer mon devis", to: "/calculateur" }}
        secondary={{ label: "Voir mes réalisations", to: "/realisations" }}
      />
      <ReviewsProof />


      <div className="px-6">
        <TldrBox
          summary="Trois formules transparentes (Essentielle, Signature, Prestige) de 690 € à 2 190 €. Pas de TVA additionnelle (franchise art. 293 B CGI), acompte 30 % à la réservation, frais kilométriques inclus dans un rayon de 80 km autour de Cavaillon."
          items={[
            { label: "Formule Essentielle", value: "À partir de 690 € (5 h)" },
            { label: "Formule Signature", value: "À partir de 1 490 € (8 h, la plus demandée pour mariage)" },
            { label: "Formule Prestige", value: "À partir de 2 190 € (cérémonie + soirée)" },
            { label: "TVA / acompte", value: "Sans TVA · acompte 30 %" },
          ]}
        />
      </div>


      {/* DISPONIBILITES */}
      <section className="pb-16 px-6 max-w-5xl mx-auto">
        <div className="border border-foreground/10 bg-card p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[var(--gold)] font-semibold">
            {SAISON_LABEL}
          </p>
          <h2 className="font-serif-display text-2xl sm:text-3xl text-foreground mt-2">
            Dates encore disponibles
          </h2>
          <p className="mt-3 text-sm font-light leading-relaxed">
            Un seul événement par week-end : les samedis de pleine saison partent vite.
            Voici l'état des disponibilités mis à jour régulièrement.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DISPONIBILITES.map((d) => (
              <li
                key={d.mois}
                className={`flex items-center justify-between gap-3 border px-4 py-3 ${
                  d.status === "complet"
                    ? "border-foreground/10"
                    : "border-foreground/15"
                }`}
              >
                <span className="text-sm text-foreground">{d.mois}</span>
                <span
                  className={`text-[10px] uppercase tracking-[0.18em] whitespace-nowrap ${
                    d.status === "ouvert"
                      ? "text-[var(--gold)]"
                      : d.status === "limite"
                        ? "text-foreground"
                        : "text-foreground/85 line-through"
                  }`}
                >
                  {d.note ?? DISPO_LABELS[d.status]}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Réponse sous 24 h · Devis gratuit et sans engagement
          </p>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="pb-16 px-6 max-w-7xl mx-auto">
        <div className="grid gap-8 md:grid-cols-3">
          {PACKS.map((p, i) => (
            <div
              key={p.name}
              id={p.name.toLowerCase()}
              className={`relative border p-10 sr flex flex-col scroll-mt-24 ${
                p.featured ? "border-[var(--gold)] bg-card" : "border-foreground/10"
              }`}

              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--gold)] text-foreground px-4 py-1 text-[9px] font-semibold uppercase tracking-[0.3em]">
                  Le + demandé
                </span>
              )}
              <p className="font-serif-display italic text-foreground text-xl">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="font-serif-display text-3xl text-foreground mt-3">{p.name}</h2>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-2">{p.intro}</p>
              <p className="text-2xl text-foreground font-light mt-6 font-serif-display italic">
                {p.price}
              </p>
              <div className="flex gap-4 mt-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <span>{p.duration}</span>
                <span>·</span>
                <span>{p.capacity}</span>
              </div>
              <span className="gold-rule !w-8 my-6" />
              <ul className="space-y-3 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-3">
                    <span className="text-foreground mt-0.5">✦</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contactez-nous"
                className="mt-8 block text-center bg-[var(--gold)] text-foreground py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
              >
                Demander ce pack
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-20 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-foreground">Comparatif détaillé</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">
              Tableau <span className="italic">comparatif</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="overflow-x-auto sr" data-reveal>
            <table className="w-full text-sm border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-foreground/20">
                  <th className="text-left py-4 px-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
                    Caractéristique
                  </th>
                  {PACKS.map((p) => (
                    <th
                      key={p.name}
                      className={`text-center py-4 px-3 font-serif-display text-base ${
                        p.featured ? "text-[var(--gold)]" : "text-foreground"
                      }`}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? "bg-background/40" : ""}>
                    <td className="py-3 px-3 text-foreground/90 font-light">{row.label}</td>
                    {row.values.map((v, j) => (
                      <td
                        key={j}
                        className={`text-center py-3 px-3 ${
                          v === "✓" ? "text-[var(--gold)] text-lg" : "text-foreground/85 text-xs"
                        }`}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* BAR À PARFUM 2027 */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center border border-[var(--gold-line)] rounded-lg overflow-hidden">
          <img src={barParfumImg} alt="Bar à parfum fleuri L'Atelier Olfactif pour mariage en Provence" className="w-full h-full min-h-64 object-cover" loading="lazy" />
          <div className="p-8">
            <p className="eyebrow text-[var(--gold)]">Nouveauté 2027</p>
            <h2 className="font-serif-display text-3xl text-foreground mt-3">Bar à parfum <span className="italic">L'Atelier Olfactif</span></h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Une animatrice dédiée guide vos invités pendant le cocktail : ils découvrent les essences, choisissent leur fragrance et repartent avec leur flacon étiqueté.
            </p>
            <ul className="mt-5 space-y-1.5 text-sm text-foreground">
              <li>Essentielle — dès 12 € / invité (2 h, 4 fragrances)</li>
              <li>Signature — dès 14 € / invité (2 h 30, 6 fragrances, déco florale)</li>
              <li>Prestige — dès 17 € / invité (3 h, flacon 30 ml)</li>
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">Minimum 50 invités · commande minimum 600 €.</p>
            <p className="mt-4 rounded-sm bg-[var(--gold)]/15 p-3 text-sm text-foreground">
              Offre de lancement : pour les 5 premiers mariages réservés, étiquettes aux prénoms des mariés offertes.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/calculateur" className="rounded-sm bg-[var(--gold)] px-5 py-3 text-sm font-semibold text-[var(--ink)]">Ajouter à mon devis</Link>
              <a href="https://latelierolfactif.fr/" target="_blank" rel="noopener" className="rounded-sm border border-foreground/20 px-5 py-3 text-sm text-foreground">Voir le site</a>
            </div>
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-foreground">Options à la carte</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Personnalisez votre <span className="italic">formule</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-foreground/10 sm:grid-cols-2 lg:grid-cols-3 border border-foreground/10">
          {ADDONS.map((a, i) => (
            <div key={a.name} className="bg-background p-6 sr" data-reveal style={{ transitionDelay: `${i * 50}ms` }}>
              <div className="flex justify-between items-baseline gap-3">
                <h3 className="font-serif-display text-lg text-foreground">{a.name}</h3>
                <span className="text-[var(--gold)] font-serif-display italic text-sm whitespace-nowrap">{a.price}</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed font-light">{a.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground font-light mt-10 max-w-2xl mx-auto">
          Pack DJ + photobooth : ajouté à une prestation DJ, la livraison, l'installation et le démontage
          de la borne sont offerts, et j'anime le photobooth pendant la soirée.{" "}
          <Link to="/location-photobooth-avignon" className="text-[var(--gold)] underline underline-offset-4">
            Voir la page photobooth
          </Link>
          .
        </p>
        <p className="text-center text-xs text-muted-foreground mt-12 italic max-w-2xl mx-auto">
          Frais de déplacement calculés depuis Cavaillon. TVA non applicable, art. 293 B du CGI.
        </p>
        <p className="text-center text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
          Pour comprendre ce qui compose la facture, consultez le guide complet des{" "}
          <Link
            to="/actualites/$slug"
            params={{ slug: "prix-dj-mariage-provence-guide-complet-2026" }}
            className="text-[var(--gold)] underline underline-offset-4"
          >
            tarifs d'un DJ de mariage en Provence
          </Link>
          .
        </p>

      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-foreground">Questions fréquentes sur les tarifs</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">FAQ tarifs</h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="space-y-3">
            {PRICING_FAQ.map((f) => (
              <details key={f.q} className="group border border-foreground/10 bg-background p-5 sr" data-reveal>
                <summary className="cursor-pointer font-serif-display text-lg text-foreground flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 px-6 bg-cover bg-center border-y border-foreground/10"
        style={{ backgroundImage: `url(${receptionImg})` }}
      >
        <div className="bg-background/85 py-16 px-8 max-w-2xl mx-auto text-center sr" data-reveal>
          <h2 className="font-serif-display text-3xl text-foreground">
            Besoin d'une <span className="italic text-foreground">prestation sur mesure</span> ?
          </h2>
          <p className="text-sm text-muted-foreground leading-loose mt-4 font-light">
            Contactez DJ Val pour un devis personnalisé, sans engagement, sous 24h.
          </p>
          <Link
            to="/contactez-nous"
            className="mt-8 inline-block bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Devis gratuit
          </Link>
        </div>
      </section>

      <RelatedLinks group="tarifs" />

      <SeoAccordion
        eyebrow="Tarifs DJ Val · Aller plus loin"
        title={<>Comprendre <span className="italic">les tarifs</span></>}
        intro="Ce qui fait varier le prix d'une prestation DJ en Provence — et pourquoi nos tarifs incluent le matériel, la livraison et l'installation."
        sections={DJ_VAL_SECTIONS}
      />
    </div>
  );
}
